// Reusable "confirm it's you" step-up dialog. Mount it next to any sensitive
// action: open it, and call the action from onVerified. It asks for the ONE
// factor the account requires (two-factor code, else password) and never
// decides that itself — the server says which via /auth/reauthenticate/status.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck } from 'lucide-react';
import { getApiErrorMessage } from '@/lib/apiError';
import { getReauthStatus, reauthenticate, type ReauthMethod } from '@/services/reauthService';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/features/shared/components/ui/dialog';

export interface ReauthDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called once a fresh proof is recorded (or one already stands). */
  onVerified: () => void;
  title?: string;
  /** What the proof unlocks — shown so the user knows why they're being asked. */
  description?: string;
}

export const ReauthDialog: React.FC<ReauthDialogProps> = ({ open, onClose, onVerified, title, description }) => {
  const { t } = useTranslation('common');
  const [method, setMethod] = useState<ReauthMethod | null | 'loading'>('loading');
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setValue('');
    setError(null);
    setMethod('loading');
    getReauthStatus()
      .then((st) => {
        if (cancelled) return;
        // A proof from a moment ago still stands — don't ask twice.
        if (st.reauthenticated) {
          onVerified();
          return;
        }
        setMethod(st.method);
      })
      .catch((e) => {
        if (cancelled) return;
        setMethod(null);
        setError(getApiErrorMessage(e, t('reauth.statusFailed', 'Could not check your sign-in status.')));
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async (ev?: React.FormEvent) => {
    ev?.preventDefault();
    if (method !== 'totp' && method !== 'password') return;
    const v = value.trim();
    if (!v) return;
    setBusy(true);
    setError(null);
    try {
      await reauthenticate(method === 'totp' ? { code: v } : { password: v });
      setValue('');
      onVerified();
    } catch (e) {
      setError(getApiErrorMessage(e, t('reauth.failed', 'Could not confirm your identity.')));
    } finally {
      setBusy(false);
    }
  };

  const isTotp = method === 'totp';

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-sm">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-primary-500" aria-hidden />
              {title ?? t('reauth.title', 'Confirm it’s you')}
            </DialogTitle>
            <DialogDescription>
              {description ?? t('reauth.description', 'This is a sensitive action, so please confirm your identity first.')}
            </DialogDescription>
          </DialogHeader>

          {method === 'loading' ? (
            <p className="text-sm text-muted-foreground">{t('loading', 'Loading…')}</p>
          ) : method === null ? (
            <p className="text-sm text-danger" role="alert">
              {error ??
                t(
                  'reauth.noMethod',
                  'This account has no password or two-factor method to confirm with. Set a password or enable two-factor authentication in Settings first.',
                )}
            </p>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="reauth-factor">
                {isTotp ? t('reauth.codeLabel', 'Two-factor code') : t('reauth.passwordLabel', 'Your password')}
              </Label>
              <Input
                id="reauth-factor"
                type={isTotp ? 'text' : 'password'}
                inputMode={isTotp ? 'numeric' : undefined}
                autoComplete={isTotp ? 'one-time-code' : 'current-password'}
                autoFocus
                maxLength={isTotp ? 8 : 256}
                placeholder={isTotp ? '123456' : undefined}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                disabled={busy}
              />
              {error && (
                <p className="text-sm text-danger" role="alert">
                  {error}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                {t('reauth.validFor', 'Once confirmed, you won’t be asked again for a few minutes.')}
              </p>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
              {t('cancel', 'Cancel')}
            </Button>
            {(method === 'totp' || method === 'password') && (
              <Button type="submit" disabled={busy || !value.trim()}>
                {busy ? t('reauth.confirming', 'Confirming…') : t('reauth.confirm', 'Confirm')}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ReauthDialog;
